import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types/filters';
import { vblForecastApi } from '@/services/api/vblForecastApi';
import type { VblForecastResult, VblForecastConfiguration } from '@/types/vblForecast';
import { DEFAULT_VBL_FORECAST_CONFIG } from '@/types/vblForecast';
import type { TurbineToForecast } from '@/types/vbl';
import type { TurbineWorkspaceMapping } from '@/types/turbineWorkspaceMapping';
import { analysisApi, type VblForecastItemRequest } from '@/services/api/analysisApi';

/**
 * Build the per-item VBL scope (UUID-based `VblFilter`) the server fits the
 * Johnson Weibull from (Phase 4B'). This replaces the old client-side fit: the
 * legacy calculator fetched VBL records scoped by the mapping's major/sub
 * COMPONENT NAMES + the turbine's site NAME and fitted Johnson locally; the
 * server-side path is UUID-based, so we send the mapping's component UUID(s) +
 * the turbine's site UUID instead.
 *
 * A major-only mapping (no specific sub-component) sends an empty `component_id`
 * — mirroring the legacy name-filter, which only set `componentsMinor` when the
 * mapping had a `component_name`. An absent site UUID likewise sends an empty
 * `site_id` (the legacy only set `sites` when `site_name` was present).
 */
function buildVblFilter(
  turbine: TurbineToForecast,
  mapping: TurbineWorkspaceMapping,
): VblForecastItemRequest['filter'] {
  // The `major_component_id` / `component_id` / `site_id` truthy guards below are
  // LOAD-BEARING. `findBestComponentMapping` Priority 2 (the exact-component match)
  // matches purely on `m.component_id` and never inspects `m.major_component_id`, so
  // a mapping with an empty/absent `major_component_id` CAN reach this function. On
  // that path the guard correctly omits the major (emits `[]`) rather than sending
  // `['']`. The same applies to `component_id` (a major-only mapping has none) and
  // `site_id` (a turbine may carry no site UUID). An empty `[]` scopes the server
  // fit to "all in scope" for that dimension; `['']` would filter on a bogus empty
  // string. Do NOT remove these guards.
  return {
    major_component_id: mapping.major_component_id ? [mapping.major_component_id] : [],
    component_id: mapping.component_id ? [mapping.component_id] : [],
    site_id: turbine.site_id ? [turbine.site_id] : [],
  };
}

/**
 * Find the best component mapping for a turbine based on component hierarchy
 * Priority: Major + Sub-component > Specific component only > Major component only > None (undefined)
 */
function findBestComponentMapping(
  turbine: TurbineToForecast,
  mappings: TurbineWorkspaceMapping[]
): TurbineWorkspaceMapping | undefined {
  // Get all mappings for this turbine
  const turbineMappings = mappings.filter(m => m.turbine_id === turbine.turbine_id);

  if (turbineMappings.length === 0) {
    return undefined;
  }

  // Helper function to safely check and compare values
  const isValidValue = (value: unknown): boolean => {
    return value !== null && value !== undefined && String(value).trim() !== '';
  };

  // Priority 1: Major component + Sub-component match (most specific)
  const majorAndSubMatch = turbineMappings.find(m => {
    const mappingMajorValid = isValidValue(m.major_component_id);
    const mappingComponentValid = isValidValue(m.component_id);
    const turbineMajorValid = isValidValue(turbine.major_component_id);
    const turbineComponentValid = isValidValue(turbine.component_id);

    return mappingMajorValid && mappingComponentValid &&
      turbineMajorValid && turbineComponentValid &&
      m.major_component_id === turbine.major_component_id &&
      m.component_id === turbine.component_id;
  });

  if (majorAndSubMatch) {
    return majorAndSubMatch;
  }

  // Priority 2: Exact specific component match (without requiring major component match)
  const exactComponentMatch = turbineMappings.find(m => {
    const mappingComponentValid = isValidValue(m.component_id);
    const turbineComponentValid = isValidValue(turbine.component_id);
    return mappingComponentValid && turbineComponentValid && m.component_id === turbine.component_id;
  });

  if (exactComponentMatch) {
    return exactComponentMatch;
  }

  // Priority 3: Major component only match (fallback)
  const majorComponentMatch = turbineMappings.find(m => {
    const mappingMajorValid = isValidValue(m.major_component_id);
    const mappingComponentInvalid = !isValidValue(m.component_id); // Only major, no sub-component
    const turbineMajorValid = isValidValue(turbine.major_component_id);
    return mappingMajorValid && mappingComponentInvalid && turbineMajorValid &&
      m.major_component_id === turbine.major_component_id;
  });

  if (majorComponentMatch) {
    return majorComponentMatch;
  }

  // No valid component mapping found
  return undefined;
}

/**
 * Hook for VBL forecast calculation.
 *
 * The per-turbine Monte-Carlo VBL compute AND the per-turbine Johnson fit now
 * run server-side at POST /api/v2/analysis/forecast/vbl (Phase 4B'). This hook
 * only does the turbine -> workspace mapping RESOLUTION client-side: for each
 * mapped turbine whose workspace has params it builds a per-item `VblFilter`
 * (the fit SCOPE) and sends the batch, then maps the surviving server results
 * back. Unmapped turbines (or mapped turbines whose workspace has no params)
 * never reach the wire. The server itself DROPS any sent turbine whose fit has
 * < 2 valid records or is degenerate — exactly the cases the legacy calculator
 * resolved to null and dropped — so the surviving `results` match the legacy
 * forecast set.
 */
export function useVblForecastCalculation({
  filters = DEFAULT_FAILURE_FILTERS,
  configuration = DEFAULT_VBL_FORECAST_CONFIG,
  autoCalculate = false
}: {
  filters?: FailureFilters;
  configuration?: VblForecastConfiguration;
  autoCalculate?: boolean;
} = {}) {
  const [forecastResults, setForecastResults] = useState<VblForecastResult[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [progressText, setProgressText] = useState('');

  // Query for VBL forecast data
  const {
    data: vblForecastData,
    isLoading: isLoadingData,
    error: dataError,
    refetch: refetchData
  } = useQuery({
    queryKey: ['vbl-forecast-data', filters],
    queryFn: () => vblForecastApi.getVblForecastData(filters),
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });

  // Calculate forecast
  const calculateForecast = useCallback(async (
    selectedTurbines?: TurbineToForecast[],
    selectedWorkspaceId?: string
  ) => {
    if (!vblForecastData || vblForecastData.turbinesToForecast.length === 0) {
      console.warn('No VBL forecast data available');
      return;
    }

    setIsCalculating(true);
    setProgressText('Calculating forecast...');

    try {
      const turbinesToProcess = selectedTurbines || vblForecastData.turbinesToForecast;

      // Build one batch item per MAPPED turbine whose workspace has params, with
      // a per-item `VblFilter` scoping the SERVER-SIDE Johnson fit. The fit/MC is
      // no longer run client-side. Turbines with no mapping (or whose workspace
      // has no params) are excluded here; turbines whose server-side fit has < 2
      // valid records or is degenerate are dropped BY THE SERVER (no result
      // emitted) — together these match the legacy `result !== null` forecast set.
      const items: VblForecastItemRequest[] = [];

      for (const turbine of turbinesToProcess) {
        const mapping = findBestComponentMapping(turbine, vblForecastData.mappings);

        // Gate exactly as before: valid mapping + workspace has params +
        // (optional) workspace filter. The workspace-params gate is PRESERVED
        // from the legacy calculator (it only forecast a turbine whose mapped
        // workspace had `workspaceParameters[ws]`); dropping it would forecast
        // turbines the old code never did, so we keep it client-side.
        if (
          mapping &&
          vblForecastData.workspaceParameters[mapping.workspace_id] &&
          (!selectedWorkspaceId || mapping.workspace_id === selectedWorkspaceId)
        ) {
          const currentDegradationTime = vblForecastApi.calculateCurrentDegradationTime(turbine);

          items.push({
            turbine,
            current_degradation_time: currentDegradationTime,
            has_mapping: true,
            workspace_mapping: mapping,
            // The legacy Monte-Carlo result never populated lastInspectionSeverity,
            // so send null to keep the server echo undefined/null (no behaviour change).
            last_inspection_severity: null,
            // Fit SCOPE: the mapping's component UUID(s) + the turbine's site UUID.
            // The server fetches VBL records for this scope and fits Johnson itself.
            filter: buildVblFilter(turbine, mapping),
            // Johnson-fit CI level — the legacy passed this to calculateJohnsonVbl.
            confidence_level: configuration.confidenceLevel,
          });
        }
      }

      if (items.length === 0) {
        setForecastResults([]);
        setProgressText('Forecast calculation completed');
        return;
      }

      // Single batch call. Fresh seed per run preserves the engine's per-run
      // non-determinism (the old MC engine seeded itself anew each run).
      const response = await analysisApi.getVblForecast(items, configuration, Date.now());

      // Preserve the legacy invariant: `forecastResults` only ever held REAL
      // forecasts. Under Phase 4B' the server DROPS turbines whose fit fails (no
      // result emitted, no sentinel), so `results` is already all-real. The
      // `hasMapping && predictedFailureDate` filter is retained as a defensive
      // guard (and to mirror the page's own filter guard) so any future sentinel
      // shape would still be excluded.
      setForecastResults(
        (response.results ?? []).filter((r) => r.hasMapping && r.predictedFailureDate),
      );
      setProgressText('Forecast calculation completed');
    } catch (error) {
      console.error('VBL forecast calculation failed:', error);
      setProgressText('Forecast calculation failed');
    } finally {
      setIsCalculating(false);
    }
  }, [vblForecastData, configuration]);

  // Clear results
  const clearResults = useCallback(() => {
    setForecastResults([]);
    setProgressText('');
  }, []);

  // Validation
  const canCalculate = useMemo(() => {
    return !isLoadingData &&
      !isCalculating &&
      vblForecastData &&
      vblForecastData.turbinesToForecast.length > 0 &&
      Object.keys(vblForecastData.workspaceParameters).length > 0;
  }, [isLoadingData, isCalculating, vblForecastData]);

  const validationErrors = useMemo(() => {
    const errors: string[] = [];

    if (!vblForecastData) {
      errors.push('No forecast data available');
    } else {
      if (vblForecastData.turbinesToForecast.length === 0) {
        errors.push('No turbines need forecasting');
      }
      if (Object.keys(vblForecastData.workspaceParameters).length === 0) {
        errors.push('No VBL analysis workspaces with valid parameters found');
      }
    }

    return errors;
  }, [vblForecastData]);

  // Summary statistics - only count turbines with valid component mappings
  const summaryStats = useMemo(() => {
    if (!vblForecastData) return null;

    const totalTurbines = vblForecastData.turbinesToForecast.length;

    // Count turbines with valid component mappings
    const mappedTurbines = vblForecastData.turbinesToForecast.filter(turbine => {
      const mapping = findBestComponentMapping(turbine, vblForecastData.mappings);
      return mapping && vblForecastData.workspaceParameters[mapping.workspace_id];
    }).length;

    const workspacesWithParameters = Object.keys(vblForecastData.workspaceParameters).length;

    return {
      totalTurbines,
      mappedTurbines,
      workspacesWithParameters
    };
  }, [vblForecastData]);

  // Auto-calculate when enabled and the underlying forecast data reference changes.
  // Tracking the last-processed reference (rather than gating on forecastResults.length === 0)
  // means a refetch — e.g. after creating/editing a record via the modal — re-triggers the calc.
  // The in-flight ref is a synchronous guard: `canCalculate` derives from `isCalculating`
  // state, which propagates asynchronously, so without this two refetches in quick succession
  // could spawn overlapping calculations.
  const lastCalculatedDataRef = useRef<typeof vblForecastData>();
  const calculationInFlightRef = useRef(false);
  useEffect(() => {
    if (!autoCalculate || !canCalculate) return;
    if (calculationInFlightRef.current) return;
    if (vblForecastData === lastCalculatedDataRef.current) return;

    const dataAtStart = vblForecastData;
    calculationInFlightRef.current = true;
    calculateForecast().finally(() => {
      lastCalculatedDataRef.current = dataAtStart;
      calculationInFlightRef.current = false;
    });
  }, [autoCalculate, canCalculate, vblForecastData, calculateForecast]);

  return {
    // Data
    turbinesToForecast: vblForecastData?.turbinesToForecast || [],
    workspaceMappings: vblForecastData?.mappings || [],
    workspaceParameters: vblForecastData?.workspaceParameters || {},
    forecastResults,

    // Loading states
    isLoadingData,
    isCalculating,
    progressText,

    // Actions
    calculateForecast,
    clearResults,
    refetchData,

    // Validation
    canCalculate,
    validationErrors,
    summaryStats,

    // Errors
    dataError
  };
}
